import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { type FC, type MutableRefObject, type ReactNode, useRef } from 'react';

import { Button } from '../Button/Button';
import { Modal, ModalFooter } from '../Modal/Modal';

export type AlertDialogVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback when the dialog should close
   */
  onClose: () => void;

  /**
   * Dialog title
   */
  title: string;

  /**
   * Dialog message/description
   */
  message: ReactNode;

  /**
   * OK button text
   * @default 'OK'
   */
  okText?: string;

  /**
   * Dialog variant that affects icon styling
   * @default 'info'
   */
  variant?: AlertDialogVariant;
}

const variantConfig = {
  info: {
    icon: InformationCircleIcon,
    iconBgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  success: {
    icon: CheckCircleIcon,
    iconBgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconBgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  error: {
    icon: XCircleIcon,
    iconBgColor: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
  },
};

/**
 * AlertDialog component
 *
 * A modal dialog for displaying alerts/messages. Replaces native window.alert().
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <AlertDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Error"
 *   message="Please fill in all required fields."
 *   variant="error"
 * />
 * ```
 */
export const AlertDialog: FC<AlertDialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  okText = 'OK',
  variant = 'info',
}) => {
  const okButtonRef = useRef<HTMLButtonElement>(null);
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      initialFocus={okButtonRef as MutableRefObject<HTMLElement | null>}
    >
      <div className="flex gap-4">
        <div
          className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full ${config.iconBgColor}`}
        >
          <Icon className={`w-6 h-6 ${config.iconColor}`} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">{message}</div>
        </div>
      </div>

      <ModalFooter>
        <Button ref={okButtonRef} type="button" variant="primary" onClick={onClose}>
          {okText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AlertDialog.displayName = 'AlertDialog';
